import React, { ReactElement } from 'react';
import { objectUtils } from '@codametrix/ui-common';
import classnames from 'classnames';
import { Conditional } from '@codametrix/ui-components';

export type NotesSidebarProps = {
  activeNote: CMxCommonApp.DecoratedNote;
  documentMetadata?: CMxAPI.DocumentMetadata[];
  onSelect?: (docMetadata: CMxAPI.DocumentMetadata) => void;
  isIPC?: boolean;
  capabilities?: Record<string | number | symbol, CMxCommonApp.Capability>;
  children?: ReactElement;
  grouped?: boolean;
};

const DEFAULT_PROPS = {
  documentMetadata: [],
  grouped: false,
  onSelect: (docMetadata: CMxAPI.DocumentMetadata) => {}
};

const DocCategories: {
  [key: string]: string;
} = {
  DFT: 'DFTs',
  DFT_IN: 'Inbound DFTs',
  DFT_OUT: 'Outbound DFTs',
  NTE: 'Notes',
  NOTE: 'Notes',
  HL7: 'HL7 Messages',
  DFT_EHR: 'DFT EHRs'
};

const NotesSidebar = (passed: NotesSidebarProps) => {
  const props = { ...DEFAULT_PROPS, ...passed };
  const {
    documentMetadata,
    onSelect,
    activeNote,
    isIPC,
    capabilities,
    grouped
  } = props;

  //@ts-ignore
  const prepped = (documentMetadata || []).map((doc) => {
    // Document categories are not named for end users.
    // Supplant with our own category names, shown above.
    // But if someone adds a document category we don't want to
    // blow up.
    const rawCategory =
      doc.mimeType === 'text/html' ? 'HTML' : doc.documentCategory;
    return {
      ...doc,
      documentCategory: DocCategories[rawCategory] || rawCategory
    };
  });

  let preppedDocs;

  if (grouped) {
    const sortGroupedDocs = (a: string, b: string) => {
      if (a === b) return 0;
      if (a === 'HL7 Messages' || a === 'Notes') return -1;
      if (b === 'HL7 Messages' || b === 'Notes') return 1;

      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    };
    preppedDocs = objectUtils.groupBy(prepped, 'documentCategory');
    Object.keys(preppedDocs).sort(sortGroupedDocs);
  } else {
    preppedDocs = prepped;
  }

  const buildDocList = (docs) => {
    return docs.map((document, idx) => {
      const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        onSelect(document);
      };

      const isActive = activeNote?.id === document.id;
      const styleClasses = classnames([
        { 'is-active': isActive },
        'sidebar-link'
      ]);

      return (
        <li key={document.documentGuid.uuid} className={styleClasses}>
          <a href="#/something" onClick={onClick}>
            <span className="document-name">
              {document.documentName ?? 'Name N/A'}
            </span>

            <span className="document-description">
              {document.documentDescription}
            </span>
            <span className="document-status">{document.documentStatus}</span>
          </a>
        </li>
      );
    });
  };

  let content;
  if (grouped) {
    content = Object.keys(preppedDocs).map((key) => {
      const docs = preppedDocs[key];
      const items = buildDocList(preppedDocs[key]);

      return (
        <div key={key}>
          <span className="note-doc-category" style={{ display: 'none' }}>
            {docs[0].documentCategory}
          </span>
          <ul className="note-sidebar-list">{items}</ul>
        </div>
      );
    });
  } else {
    content = (
      <div>
        <ul className="note-sidebar-list">{buildDocList(preppedDocs)}</ul>{' '}
      </div>
    );
  }

  return (
    <div className="notes-sidebar">
      <div className="sidebar-fixed">
        <div className="sidebar-container">
          <div className="sidebar-inner">
            {
              <Conditional every={[documentMetadata?.length > 1]}>
                <>{content}</>
              </Conditional>
            }

            {props.children}
          </div>
        </div>
      </div>
    </div>
  );
};

export { NotesSidebar };
